/**
 * The View that shows the list of the Invoices made by the vendor
 */
import { assign, chain, debounce, get } from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import React, { Component } from 'react';
import { Button, Icon, HideText } from '@fashionnovateam/nova-ui-toolkit';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import historyProps from '../../Lib/Utils/PropTypes/history-props';
import locationProps from '../../Lib/Utils/PropTypes/location-props';
import matchProps from '../../Lib/Utils/PropTypes/match-props';
import { vendorBillsExport } from '../../Lib/Utils/Table/table-export';
import UserRole from '../../Lib/Constants/roles.ts';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import VendorBillDataGrid from './VendorBillDataGrid.tsx';

class VendorBills extends Component {
  constructor(props) {
    super(props);

    const { getVendorBills, location } = this.props;
    const params = decodeLocation(location.search);

    this.state = {
      exporting: false,
      sortedBy: params.sortBy,
    };

    this.handlePageChange = this.handlePageChange.bind(this);
    this.fetchVendorBills = this.fetchVendorBills.bind(this);
    this.removeSortParams = this.removeSortParams.bind(this);
    this.exportBills = this.exportBills.bind(this);
    this.fetchVendorBillsHeader = this.fetchVendorBillsHeader.bind(this);

    getVendorBills(this.currentUserId(), location.search.slice(1));
  }

  /**
   * Reads the location prop and returns the user id from the url
   */
  currentUserId() {
    const { location } = this.props;
    return location.pathname.split('/')[2];
  }

  /**
   * Adds query parameters to the url to update the query to our api and calls
   * fetchVendorBills to fetch the invoices from the database
   *
   * @param {string} name the name of the query param being added to the url
   * @param {string} value the value of the parameter being added to the url
   */
  fetchVendorBills(name, value) {
    const {
      getVendorBills,
      history,
      location: { search: params, pathname },
    } = this.props;

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ [name]: value })
      .thru((p) => (name === 'search' ? assign(p, { page: 1 }) : p))
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getVendorBills(this.currentUserId(), search);
  }

  removeSortParams() {
    const {
      getVendorBills,
      history,
      location: { search: params, pathname },
    } = this.props;

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: undefined, sortOrder: undefined })
      .pickBy()
      .thru(queryString.encode)
      .value();

    history.push({ pathname, search });

    getVendorBills(this.currentUserId(), search);
  }

  /**
   * A function that updates the url when different parameters in the component
   * are updated. Calls getVendorBills to get all vendor bills that
   * match the params. Called when sort is altered using table headers
   * @param {string} sortBy the name of the parameter to be added to the url
   * @param {string} sortOrder the order parameter (ex. 'ASC'/'DESC')
   */
  fetchVendorBillsHeader(e, field) {
    const {
      getVendorBills,
      history,
      location: { search: params, pathname },
    } = this.props;

    const sortParams = decodeLocation(params);
    const { sortBy, sortOrder } = sortParams;

    let newSortOrder;
    if (!sortOrder || field !== sortBy) {
      newSortOrder = sortOrder || 'ASC';
    } else {
      newSortOrder = sortOrder === 'DESC' ? 'ASC' : 'DESC';
    }

    const search = chain(params.slice(1))
      .thru(queryString.parse)
      .assign({ sortBy: field, sortOrder: newSortOrder })
      .pickBy()
      .thru(queryString.encode)
      .value();

    this.setState({ sortedBy: field });
    history.push({ pathname, search });

    getVendorBills(this.currentUserId(), search);
  }

  /**
   * Updates the url to increment, decrement, or navigate directly to a
   * specific results page
   *
   * @param {number} page the next page to load in the table view
   */
  handlePageChange(page) {
    this.fetchVendorBills('page', page);
  }

  exportBills() {
    const userId = this.currentUserId();
    this.setState({ exporting: true });
    vendorBillsExport(userId).then(() => {
      this.setState({ exporting: false });
    });
  }

  render() {
    const { count, location, vendorBills, loading } = this.props;
    const params = decodeLocation(location.search);
    const { exporting, sortedBy } = this.state;
    const seachValue = queryString.parse(location.search.slice(1)).search || '';

    const isInfluencer =
      get(this.props, 'vendor.contacts[0].role') === UserRole.INFLUENCER;

    return (
      <Page.Container>
        <Header.Major icon="bank" title="Invoices" />
        <Header.Minor
          search={{
            value: seachValue,
            onChange: debounce(
              (value) => this.fetchVendorBills('search', value),
              500,
            ),
          }}
        >
          {exporting ? (
            <Icon.LoadingCircle />
          ) : (
            <Button size="sm" color="secondary" onClick={this.exportBills}>
              <Icon name="download/download-down" />
              <HideText notAbove="lg">Export</HideText>
            </Button>
          )}
        </Header.Minor>
        <Page.Content>
          <VendorBillDataGrid
            isInfluencer={isInfluencer}
            rows={vendorBills}
            loading={loading}
            rowCount={count}
            pageSizeOptions={[15]}
            paginationModel={{
              page: params?.page ? params.page - 1 : 0,
              pageSize: 15,
            }}
            onPaginationModelChange={(model) => {
              this.fetchVendorBills('page', model.page + 1);
            }}
            onSortModelChange={(model) => {
              if (model.length > 0) {
                this.fetchVendorBillsHeader(undefined, model[0].field);
              } else {
                this.removeSortParams();
              }
            }}
            sortModel={
              sortedBy
                ? [
                    {
                      field: sortedBy,
                      sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                    },
                  ]
                : []
            }
          />
        </Page.Content>
      </Page.Container>
    );
  }
}

VendorBills.propTypes = {
  location: locationProps.isRequired,
  history: historyProps.isRequired,
  match: matchProps.isRequired,
  loading: PropTypes.bool.isRequired,
  vendorBills: PropTypes.arrayOf(
    PropTypes.shape({
      invoice_number: PropTypes.string,
      status: PropTypes.string,
      po_internal_id: PropTypes.string,
      po_number: PropTypes.string,
      total: PropTypes.number,
    }).isRequired,
  ).isRequired,
  count: PropTypes.number.isRequired,
  getVendorBills: PropTypes.func.isRequired,
  vendor: PropTypes.shape({}).isRequired,
};

export default VendorBills;
